// appComposer namespace — the per-post consent screen (SPEC A7; paczka 12, artboard 10).
// Keys mirror pl/appComposer.ts exactly. See that file for why this copy is part
// of the compliance surface rather than decoration.
export const appComposer = {
  title: 'Confirm your posts',
  subtitle: 'Posts: {n} of {total}',
  batchNote: 'You can confirm up to {limit} posts at a time.',
  batchNote_one: 'You can confirm up to {limit} post at a time.',
  batchNote_few: 'You can confirm up to {limit} posts at a time.',
  batchNote_many: 'You can confirm up to {limit} posts at a time.',
  batchRange: 'You are confirming {from}-{to} of {total}',
  afterBatch: 'after this set {n} of {total} remain',
  postedAs: 'Posting as',
  goesOut: 'Goes out {when}',

  costLabel: 'Cost to publish',
  costBreakdown: 'Posts: {n} × {each} tokens',
  costBreakdown_one: 'Posts: {n} × {each} token',
  costBreakdown_few: 'Posts: {n} × {each} tokens',
  costBreakdown_many: 'Posts: {n} × {each} tokens',
  costTotal: '{n} tokens',
  costTotal_one: '{n} token',
  costTotal_few: '{n} tokens',
  costTotal_many: '{n} tokens',
  costAfter: 'Balance after you confirm',
  // 35-R2, resolution 6: the sentence under the confirm button replaces the shorter
  // `appCampaigns.detail.consentsNote`, which stays on the plan details screen.
  submitNote: 'Without consent the post will not be sent.',

  bulkTitle: 'Set for all posts in this set ({n})',
  bulkAi: 'AI label',
  // „set”, not the older English word for it: the locked dictionary owns this noun
  // in visible copy, and `forbidden_vocab_lint.py` is the gate that says so.
  bulkTitleAll: 'Set for all posts in this set - separately for each platform',
  bulkPlatform: '{platform} ({n})',
  bulkSynthetic: 'Synthetic content',
  bulkExpand: 'Expand three platforms',

  aiLabel: 'This content was made with AI',
  aiLabelNote: 'Once published this label cannot be removed.',

  processingNote: 'After you confirm, it can take a few minutes for a post to appear.',
  musicConfirm: 'By posting, you agree to TikTok’s Music Usage Confirmation',
  brandedPolicy: 'Branded Content Policy',
  agreeAll: 'By publishing, you approve every post with the settings chosen above.',

  consentPerPost: 'We store consent separately for each post - together with the visibility you chose and the version of this statement.',
  consentPerPostIg: 'We record consent per post - together with this consent’s version.',
  consentPerPostYt: 'We record consent per post - together with visibility, the "made for kids" declaration and this consent’s version.',
  cancelBefore: 'You can cancel a post until it has been handed over for sending.',

  editTitle: 'Title',
  editCaption: 'Description',
  captionCount: '{len} / {max}',
  foldMark: 'folds after {fold}',
  foldHint: 'after {fold} characters the platform folds the description - a marker, not an error',

  reviewProgress: 'Reviewed {n} of {total}',
  needsFix: '{n} to complete',
  nextIncomplete: 'To the next incomplete',
  pager: '{n} / {total}',
  prevPost: 'Previous post',
  nextPost: 'Next post',
  needsFieldsYt: '{n} YouTube posts still lack a title, visibility or the "made for kids" declaration - fill them in and the button turns on.',
  needsFieldsYt_one: '{n} YouTube post still lacks a title, visibility or the "made for kids" declaration - fill it in and the button turns on.',
  needsFieldsYt_few: '{n} YouTube posts still lack a title, visibility or the "made for kids" declaration - fill them in and the button turns on.',
  needsFieldsYt_many: '{n} YouTube posts still lack a title, visibility or the "made for kids" declaration - fill them in and the button turns on.',
  needsVisibility: '{n} posts have no visibility yet - complete them and the button switches on.',
  needsVisibility_one: '{n} post has no visibility yet - complete it and the button switches on.',
  needsVisibility_few: '{n} posts have no visibility yet - complete them and the button switches on.',
  needsVisibility_many: '{n} posts have no visibility yet - complete them and the button switches on.',
  needsAgreement: 'Tick the consent under the posts and the button switches on.',

  submit: 'Confirm {n} posts',
  submit_one: 'Confirm {n} post',
  submit_few: 'Confirm {n} posts',
  submit_many: 'Confirm {n} posts',
  submitOne: 'Confirm this post',
  remaining: '{n} posts still to confirm',
  remaining_one: '{n} post still to confirm',
  remaining_few: '{n} posts still to confirm',
  remaining_many: '{n} posts still to confirm',
  submitted: 'Confirmed {n} · {m} left',

  rejected: 'This text cannot be published',
  staleInfo: 'Showing your last known account settings - refreshing…',

  // ── session 23: three platforms in one batch (MAPA 23, artboard 03) ────────────
  subtitlePlatforms: 'TikTok, Instagram and YouTube in one set - each card has its platform’s fields.',
  format: {
    tiktok: 'post',
    instagram: 'Reel',
    youtube: 'Short',
  },

  ig: {
    foldHint: 'after 125 characters Instagram folds the caption - a marker, not an error',
    cover: 'Cover · first frame',
  },

  yt: {
    title: 'Title',
    titleRequired: 'required',
    titleMissing: 'Enter a title - YouTube requires one.',
    titleTooLong: 'A title is at most 100 characters.',
    captionHint: 'hashtags from the description become the video’s tags',
    synthetic: 'Contains synthetic content that could be mistaken for real',
    syntheticNote: 'YouTube may add a label to the video.',
    category: 'Category: Music',
  },
  plan: {
    done: 'Done',
  },
} as const
